import { FC, ReactNode, useContext, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Box, useMediaQuery, useTheme } from '@material-ui/core';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import {
  FormattedMessage,
  LimitedAccessBanner,
  Modal,
  SearchModal,
  ShoppingBag,
  ShoppingBagModal,
  TopbarDesktop,
  TopbarMobile,
  TopbarMobileMenu,
} from '..';
import AppContext from '../../context/AppContext';
import config from '../../shopConfig/config';
import { useShopConfig } from '../../hooks/shopConfig';
import { useRouteConfiguration } from '../../hooks/useRouteConfiguration';
import { createResourceLocatorString } from '../../util/routes';
import { ModalParams, parse } from '../../util/urlHelpers';
import { redirectToURLWithoutModalState } from '../../util/window';
import TopbarTransparencyContext from '../../context/TopbarTransparencyContext';
import AdminAccountMismatchBanner from '../AdminAccountMismatchBanner/AdminAccountMismatchBanner';
import { ModalType } from '../../ducks/modal.duck';
import GeneralShopConfigHighlightBanner from '../HighlightBanner/GeneralShopConfigHighlightBanner';
import PromoHighlightBanner from '../HighlightBanner/PromoHighlightBanner';
import css from './Topbar.module.css';
import MainSiteBanner from '../MainSiteBanner/MainSiteBanner';
import { useShouldShowMainSiteBanner } from '../../hooks/useShouldShowMainSiteBanner';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { Feature } from '../../util/featureFlags';
import { processReferrerData, Referrer } from '../../util/sessionHelpers/referrerSessionHelpers';
import { TreetShopNavBar } from '../TreetShopNavBar/TreetShopNavBar';
import { useIsHydrated } from '../../hooks/useIsHydrated';
import { getMaybePolyfilledResizeObserver } from '../../util/polyfills';

const TOPBAR_ELEM_ID = 'topbar';

interface GenericErrorProps {
  show: boolean;
}

const GenericError: FC<GenericErrorProps> = ({ show }) => {
  const classes = classNames(css.genericError, {
    [css.genericErrorVisible]: show,
  });
  return (
    <div className={classes}>
      <div className={css.genericErrorContent}>
        <p className={css.genericErrorText}>
          <FormattedMessage id="Topbar.genericError" />
        </p>
      </div>
    </div>
  );
};

const shouldShowTreetShopNavBar = ({
  treetId,
  isTreetShopEnabled,
  isHydrated,
}: {
  treetId: string;
  isTreetShopEnabled: boolean;
  isHydrated: boolean;
}) => {
  const { referrer } = processReferrerData();
  const isTreetShopReferred = referrer === Referrer.TreetShop;
  const isTreetShop = treetId === 'treet';

  return isTreetShopReferred && !isTreetShop && isTreetShopEnabled && isHydrated;
};

interface TopbarComponentProps {
  mainNavbarSectionClassName?: string | null;
  topbarDesktopClassName?: string | null;
  topbarMobileRootClassName?: string | null;
  topbarMobileClassName?: string | null;
  isAuthenticated: boolean;
  authInProgress: boolean;
  currentSearchParams?: Record<string, any>;
  currentUser?: any;
  currentPage?: string | null;
  history: any;
  location: any;
  onManageDisableScrolling: (componentId: string, disableScrolling: boolean) => void;
  showGenericError: boolean;
  additionalMainNavbarSectionChildren?: ReactNode;
}

const TopbarComponent: FC<TopbarComponentProps> = ({
  mainNavbarSectionClassName,
  topbarDesktopClassName,
  topbarMobileRootClassName,
  topbarMobileClassName,
  isAuthenticated,
  authInProgress,
  currentSearchParams,
  currentUser,
  currentPage,
  history,
  location,
  onManageDisableScrolling,
  showGenericError,
  additionalMainNavbarSectionChildren,
}) => {
  const { css: brandCss, isPromoBelowTopbar = false } = useShopConfig();
  const routes = useRouteConfiguration();
  const { treetId } = useContext(AppContext);
  const { isTransparentTopbar, isFullBleedTopbar } = useContext(TopbarTransparencyContext);
  const intl = useIntl();

  const isTreetShopEnabled = useFeatureFlags(Feature.TreetShop);

  const { activeModal } = useSelector((state: any) => state.modal);

  const theme = useTheme();
  const isSmallScreenSize = useMediaQuery(theme.breakpoints.down('sm'));

  const { isHydrated } = useIsHydrated();

  const showTreetShopNavBar = shouldShowTreetShopNavBar({
    isTreetShopEnabled,
    treetId,
    isHydrated,
  });

  const showMainSiteBanner = useShouldShowMainSiteBanner();

  const handleMobileMenuClose = () => {
    redirectToURLWithoutModalState(history, location, ModalParams.MobileMenu);
  };

  const handleMobileShoppingBagClose = () => {
    redirectToURLWithoutModalState(history, location, ModalParams.MobileShoppingBag);
  };

  const treetShopBannerSectionRef = useRef<HTMLDivElement | null>(null);
  const mainNavbarSectionContentRef = useRef<HTMLDivElement | null>(null);
  const bottomSentinelElementRef = useRef<HTMLDivElement | null>(null);

  // Set various navbar section heights as CSS variables in sync with elements resizing.
  useEffect(() => {
    const calculateHeights = () => {
      const treetShopHeight =
        treetShopBannerSectionRef.current?.getBoundingClientRect().height ?? 0;
      const mainNavbarContentHeight =
        mainNavbarSectionContentRef.current?.getBoundingClientRect().height ?? 0;
      const totalStickyHeight = treetShopHeight + mainNavbarContentHeight;
      const sentinelTop = bottomSentinelElementRef.current?.offsetTop ?? 0;

      const rootStyle = document.documentElement.style;
      // Variable: --dynamicallyCalculatedNavbarStickySectionsHeight
      // The height of all the sticky sections in the navbar.
      rootStyle.setProperty(
        '--dynamicallyCalculatedNavbarStickySectionsHeight',
        `${totalStickyHeight}px`
      );
      // Variable: --dynamicallyCalculatedTreetShopBannerSectionHeight
      // The height of only the Treet Shop Banner.
      rootStyle.setProperty(
        '--dynamicallyCalculatedTreetShopBannerSectionHeight',
        `${treetShopHeight}px`
      );
      // Variable: --dynamicallyCalculatedAllNavbarSectionsInitialHeight
      // The height of all the navbar sections before any have ducked.
      rootStyle.setProperty(
        '--dynamicallyCalculatedAllNavbarSectionsInitialHeight',
        `${sentinelTop}px`
      );
    };

    let resizeObserver: ResizeObserver | null = null;

    const setupResizeObserver = async () => {
      const ResizeObserverConstructor = await getMaybePolyfilledResizeObserver();
      resizeObserver = new ResizeObserverConstructor(() => {
        calculateHeights();
      });

      // Observe elements
      [
        treetShopBannerSectionRef.current,
        mainNavbarSectionContentRef.current,
        bottomSentinelElementRef.current,
      ]
        .filter(Boolean)
        .forEach((element) => resizeObserver?.observe(element as HTMLDivElement));

      // Initial calculation
      calculateHeights();
    };

    setupResizeObserver();

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [treetShopBannerSectionRef, mainNavbarSectionContentRef, bottomSentinelElementRef]);

  const handleSubmit = (values: any) => {
    const { search, selectedPlace } = values.location;
    const { origin, bounds } = selectedPlace;
    const originMaybe = config.sortSearchByDistance ? { origin } : {};
    const searchParams = {
      ...currentSearchParams,
      ...originMaybe,
      address: search,
      bounds,
    };
    history.push(createResourceLocatorString('SearchPage', routes, {}, searchParams));
  };

  const {
    [ModalParams.MobileMenu]: mobileMenuParam,
    [ModalParams.MobileShoppingBag]: mobileShoppingBagParam,
    address,
    origin,
    bounds,
  } = parse(location.search);

  const isMobileMenuOpen = isSmallScreenSize && mobileMenuParam === 'open';
  const isMobileShoppingBagOpen = isSmallScreenSize && mobileShoppingBagParam === 'open';

  const mobileMenu = (
    <TopbarMobileMenu
      isAuthenticated={isAuthenticated}
      currentUser={currentUser}
      currentPage={currentPage}
    />
  );

  // Only render current search if full place object is available in the URL params
  const locationFieldsPresent = config.sortSearchByDistance
    ? address && origin && bounds
    : address && bounds;
  const initialSearchFormValues = {
    location: locationFieldsPresent
      ? {
          search: address,
          selectedPlace: { address, origin, bounds },
        }
      : null,
  };

  const backgroundStyles = {
    bgcolor: isTransparentTopbar ? '' : brandCss?.backgroundColor || 'white',
    style: {
      transition: 'background .2s ease-out',
    },
  };

  return (
    /**
     * ⚠️ Important
     * Any sticky sections CANNOT have a container (and must be DIRECT children of a parent that takes up the entire page).
     * Otherwise they won't sticky appropriately
     */
    <>
      {/* Begin Sticky Elements */}
      {showTreetShopNavBar && (
        <div
          className={classNames(css.treetShopBannerSection, css.stickyTopbarSection)}
          ref={treetShopBannerSectionRef}
        >
          <TreetShopNavBar />
        </div>
      )}
      {/* End Sticky Elements */}

      {/* Begin Duckable Elements */}
      <div>
        {!isPromoBelowTopbar && <PromoHighlightBanner />}
        {showMainSiteBanner && <MainSiteBanner />}
        <GeneralShopConfigHighlightBanner currentPage={currentPage} />
      </div>
      {/* End Duckable Elements */}

      {/* Begin Sticky Elements */}
      <div
        id={TOPBAR_ELEM_ID}
        className={classNames(
          css.mainNavbarSection,
          css.stickyTopbarSection,
          isFullBleedTopbar && css.mainNavbarSectionFullBleed,
          mainNavbarSectionClassName
        )}
      >
        {/* 
        Main Navbar Section Content is separated because the parent 
        can have it's height overriden when the full bleed topbar is active.
        This allows us to calculate the height of the content while allowing the parent to be
          1. Sticky
          2. Have a height of 0 when transparent.
        The calculated height is then used to position sticky elements that come after.
        */}
        <div ref={mainNavbarSectionContentRef}>
          <LimitedAccessBanner currentUser={currentUser} currentPage={currentPage} />
          <AdminAccountMismatchBanner currentUser={currentUser} />
          <Box
            className={classNames(
              topbarMobileRootClassName || css.container,
              topbarMobileClassName,
              {
                [css.transparentTopbarBackground]: isTransparentTopbar,
                [css.transparentTopbarForeground]: isTransparentTopbar,
              }
            )}
            {...backgroundStyles}
          >
            <TopbarMobile />
          </Box>
          <Box
            className={classNames(css.desktop, {
              [css.transparentTopbarBackground]: isTransparentTopbar,
            })}
            {...backgroundStyles}
          >
            <TopbarDesktop
              className={topbarDesktopClassName}
              initialSearchFormValues={initialSearchFormValues}
              intl={intl}
              isAuthenticated={isAuthenticated}
              onSearchSubmit={handleSubmit}
              subdomain={treetId}
            />
          </Box>
          <Modal
            id="TopbarMobileMenu"
            isOpen={isMobileMenuOpen}
            onClose={handleMobileMenuClose}
            usePortal
            onManageDisableScrolling={onManageDisableScrolling}
          >
            {authInProgress ? null : mobileMenu}
          </Modal>
          <Modal
            id="ShoppingBag"
            containerClassName={css.modalContainer}
            isOpen={isMobileShoppingBagOpen}
            onClose={handleMobileShoppingBagClose}
            usePortal
            onManageDisableScrolling={onManageDisableScrolling}
          >
            <div className={css.mobileShoppingBagContainer}>
              <ShoppingBag isOpen={isMobileShoppingBagOpen} className={css.mobileShoppingBag} />
            </div>
          </Modal>
          <ShoppingBagModal isOpen={activeModal === ModalType.ShoppingBag} />
          <SearchModal isOpen={activeModal === ModalType.SearchModal} />
          <GenericError show={showGenericError} />
          {additionalMainNavbarSectionChildren}
        </div>
      </div>
      {/* End Sticky Elements */}

      {/* Begin Duckable Elements */}
      <div>{isPromoBelowTopbar && <PromoHighlightBanner />}</div>
      {/* End Duckable Elements */}

      {/* Use the below element to track the height of the topbar since we can't wrap in a div
      Note: This assumes that there are no sibling components before the navbar sections */}
      <div style={{ height: '0px', width: '100%' }} ref={bottomSentinelElementRef} />
    </>
  );
};

TopbarComponent.displayName = 'Topbar';

export default TopbarComponent;
